/**
 * OnboardingWelcome.jsx
 * First-time experience modal shown to new merchants after Salla App Store installation.
 * Polls for the first auto-analysis and shows performance improvement estimate.
 */
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Zap, CheckCircle2, Loader2, ArrowLeft, BarChart2,
  ShoppingBag, TrendingUp, X, Sparkles, Copy, KeyRound, LogIn
} from "lucide-react";
import { toast } from "sonner";

const POLL_INTERVAL  = 3000;   // ms
const POLL_MAX_TRIES = 40;     // 2 min max

export default function OnboardingWelcome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isNew     = searchParams.get("new") === "1";
  const installed = searchParams.get("installed") === "1";

  const [visible, setVisible]         = useState(false);
  const [analysisId, setAnalysisId]   = useState(null);
  const [analysisReady, setAnalysisReady] = useState(false);
  const [perfScore, setPerfScore]     = useState(null);
  const [isEstimated, setIsEstimated] = useState(false);
  const [pollTries, setPollTries]     = useState(0);
  const [closing, setClosing]         = useState(false);
  const [pwdCopied, setPwdCopied]     = useState(false);

  // Retrieve temp password saved by SallaInstallHandler
  const tempPwd = localStorage.getItem("salla_temp_pwd") || "";

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPwd).then(() => {
      setPwdCopied(true);
      toast.success("تم نسخ كلمة السر");
      setTimeout(() => setPwdCopied(false), 2000);
    });
  };

  // Show modal only for new installs
  useEffect(() => {
    if (installed && isNew && user) {
      setVisible(true);
      loadOnboarding();
    } else if (installed && !isNew && user) {
      // Returning merchant re-install — just clean URL
      cleanUrl();
    }
  }, [installed, isNew, user]);

  const loadOnboarding = async () => {
    try {
      const { data } = await api.get("/merchant/onboarding");
      if (data.analysis_id) {
        setAnalysisId(data.analysis_id);
        if (data.first_analysis_done) {
          setPerfScore(data.analysis?.perf_mobile ?? data.performance_score);
          setAnalysisReady(true);
        }
      }
    } catch {}
  };

  // Poll for first analysis completion
  useEffect(() => {
    if (!visible || analysisReady) return;
    if (pollTries >= POLL_MAX_TRIES) return;

    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/merchant/onboarding");
        if (data.first_analysis_done) {
          const score = data.analysis?.perf_mobile ?? data.performance_score;
          setPerfScore(score);
          setIsEstimated(data.analysis?.is_estimated);
          setAnalysisReady(true);
        } else {
          setPollTries((p) => p + 1);
        }
      } catch {
        setPollTries((p) => p + 1);
      }
    }, POLL_INTERVAL);

    return () => clearTimeout(timer);
  }, [visible, analysisReady, pollTries]);

  const cleanUrl = () => {
    setSearchParams({}, { replace: true });
  };

  const handleDismiss = async () => {
    setClosing(true);
    try {
      await api.post("/merchant/onboarding/complete");
    } catch {}
    localStorage.removeItem("salla_temp_pwd"); // clear after shown
    cleanUrl();
    setVisible(false);
    setClosing(false);
  };

  if (!visible) return null;

  // Calculate improvement potential (100 - current score = headroom)
  const headroom = perfScore != null ? Math.max(0, 100 - perfScore) : null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      dir="rtl"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-300">

        {/* ── Dark header ── */}
        <div className="bg-[#0C0C0F] px-7 py-6 relative">
          <button
            onClick={handleDismiss}
            className="absolute top-4 left-4 p-1.5 text-white/30 hover:text-white/80 transition-colors rounded-lg hover:bg-[var(--bg-card-hover)]/10"
            data-testid="onboarding-close-btn"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#004EEB] rounded-2xl grid place-items-center shadow-[0_0_24px_rgba(0,78,235,0.5)]">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-bold text-lg">مرحباً في جسر</div>
              <div className="text-white/40 text-sm">تم تثبيت التطبيق بنجاح</div>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-7 py-6 space-y-5">

          {/* Analysis status */}
          <div className={`rounded-2xl border p-5 transition-all ${
            analysisReady
              ? "bg-emerald-50 border-emerald-200"
              : "bg-blue-50 border-blue-100"
          }`}>
            {analysisReady ? (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl grid place-items-center shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <div className="text-base font-bold text-slate-900">
                    اكتمل تحليل متجرك
                  </div>
                  {headroom != null && headroom > 5 && (
                    <div className="text-slate-600 text-sm mt-1">
                      متجرك يمكن تحسين أدائه بنسبة{" "}
                      <span className="font-bold text-emerald-700 text-base">
                        {headroom}%
                      </span>
                    </div>
                  )}
                  {perfScore != null && (
                    <div className="flex items-center gap-2 mt-3">
                      <div className={`text-2xl font-black ${
                        perfScore >= 90 ? "text-emerald-600"
                        : perfScore >= 50 ? "text-amber-600"
                        : "text-red-500"
                      }`}>
                        {perfScore}
                      </div>
                      <div className="text-slate-400 text-sm">
                        / 100 — أداء الجوال
                        {isEstimated && <span className="text-xs text-amber-500 mr-1">(تقديري)</span>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl grid place-items-center shrink-0">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
                <div>
                  <div className="text-base font-bold text-slate-900">
                    جارٍ تحليل متجرك...
                  </div>
                  <div className="text-slate-500 text-sm mt-0.5">
                    نفحص أداء المتجر ونُعدّ توصيات الذكاء الاصطناعي
                  </div>
                  <div className="flex gap-1 mt-3">
                    {[0,1,2,3,4].map((i) => (
                      <div
                        key={i}
                        className="h-1 rounded-full bg-blue-200 overflow-hidden flex-1"
                      >
                        <div
                          className="h-full bg-blue-500 animate-pulse rounded-full"
                          style={{ animationDelay: `${i * 0.2}s` }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: BarChart2,  label: "تحليل الأداء",   color: "text-blue-600 bg-blue-50" },
              { icon: ShoppingBag, label: "ربط سلة",       color: "text-indigo-600 bg-indigo-50" },
              { icon: TrendingUp, label: "توصيات AI",       color: "text-emerald-600 bg-emerald-50" },
            ].map(({ icon: Icon, label, color }) => (
              <div
                key={label}
                className="flex flex-col items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100"
              >
                <div className={`w-8 h-8 rounded-lg grid place-items-center ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-xs font-semibold text-slate-700 text-center">{label}</span>
              </div>
            ))}
          </div>

          {/* Password box — shown only for new installs */}
          {tempPwd && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-blue-800 text-sm font-semibold">
                <KeyRound className="w-4 h-4 shrink-0" />
                كلمة السر الخاصة بك — احفظها الآن
              </div>
              <div className="flex items-center gap-2 bg-white rounded-xl border border-blue-100 px-3 py-2">
                <code className="flex-1 text-sm font-mono text-slate-800 select-all tracking-wider" data-testid="temp-password">
                  {tempPwd}
                </code>
                <button
                  onClick={copyPassword}
                  data-testid="copy-password-btn"
                  className="shrink-0 p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-blue-600">
                <LogIn className="w-3 h-3" />
                ادخل بهذه الكلمة مع إيميلك من صفحة <a href="/login" className="underline font-semibold">تسجيل الدخول</a>
              </div>
            </div>
          )}

          {/* Trial badge */}
          <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
            <div className="text-sm text-amber-800">
              <span className="font-bold">30 يوم تجريبي مجاناً</span> — استمتع بكل الميزات بدون قيود
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-slate-100 px-7 py-4 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            {analysisReady ? "التحليل جاهز" : "التحليل جارٍ في الخلفية..."}
          </span>
          <button
            onClick={handleDismiss}
            disabled={closing}
            data-testid="onboarding-goto-dashboard-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#004EEB] hover:bg-[#0040C1] disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-all shadow-lg shadow-[#004EEB]/25"
          >
            {closing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowLeft className="w-3.5 h-3.5" />}
            الذهاب للوحة التحكم
          </button>
        </div>

      </div>
    </div>
  );
}
